import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Banknote,
  Coins,
  HandCoins,
  ScrollText,
  ShieldCheck,
  Undo2,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  SessionLedgerEntry,
  SessionLedgerSubType,
} from "@/redux/services/pos/pos.type";
import { cn, displayPrice } from "@/lib/utils";
import {
  getLedgerReasonLabel,
  LEDGER_METADATA_LABELS,
  resolveLedgerExpenseUuid,
  resolveLedgerOrder,
} from "./ledgerUtils";

interface LedgerEntryDrawerProps {
  entry: SessionLedgerEntry | null;
  currency: string;
  onClose: () => void;
  onOpenOrder?: (order: { id: string; orderNumber: string }) => void;
  onOpenExpense?: (expenseUuid: string) => void;
}

interface SubTypeMeta {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: "positive" | "negative" | "neutral";
}

const SUBTYPE_META: Record<SessionLedgerSubType, SubTypeMeta> = {
  cash: { label: "Encaissement", icon: Banknote, tone: "positive" },
  payment_tip: { label: "Pourboire encaissé", icon: Coins, tone: "positive" },
  pay_in: { label: "Approvisionnement", icon: ArrowDownToLine, tone: "positive" },
  pay_out: { label: "Sortie de caisse", icon: ArrowUpFromLine, tone: "negative" },
  cash_drop: { label: "Dépôt coffre", icon: ShieldCheck, tone: "negative" },
  tip_in: { label: "Pourboires reçus", icon: Coins, tone: "positive" },
  tip_out: { label: "Pourboires retirés", icon: HandCoins, tone: "negative" },
  refund: { label: "Remboursement", icon: Undo2, tone: "negative" },
  correction: { label: "Correction", icon: ScrollText, tone: "neutral" },
};

const LedgerEntryDrawer = ({
  entry,
  currency,
  onClose,
  onOpenOrder,
  onOpenExpense,
}: LedgerEntryDrawerProps) => {
  const meta = entry ? SUBTYPE_META[entry.subType] : null;
  const Icon = meta?.icon ?? Banknote;

  const signed = entry?.signedAmount ?? 0;
  const reasonLabel = getLedgerReasonLabel(entry?.reason);
  const expenseUuid = entry ? resolveLedgerExpenseUuid(entry) : null;
  // Only surface human-friendly metadata (French-labelled); internal references
  // like *Uuid are never displayed.
  const metadataEntries = entry?.metadata
    ? Object.entries(entry.metadata).filter(
        ([key, v]) => v != null && v !== "" && key in LEDGER_METADATA_LABELS,
      )
    : [];

  return (
    <Sheet open={!!entry} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        {entry && meta && (
          <>
            <SheetHeader>
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0",
                    meta.tone === "positive"
                      ? "bg-success/10 text-success"
                      : meta.tone === "negative"
                      ? "bg-destructive/10 text-destructive"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <SheetTitle>{meta.label}</SheetTitle>
                  <SheetDescription>
                    {entry.subType === "payment_tip"
                      ? "Pourboire encaissé avec le paiement"
                      : entry.kind === "payment"
                      ? "Encaissement en espèces"
                      : "Mouvement de caisse"}
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>

            <div className="mt-6 space-y-5">
              <div className="rounded-lg bg-muted/40 p-4 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Impact sur les espèces
                </span>
                <span
                  className={cn(
                    "text-xl font-bold tabular-nums",
                    signed > 0
                      ? "text-success"
                      : signed < 0
                      ? "text-destructive"
                      : "text-card-foreground",
                  )}
                >
                  {signed > 0 ? "+" : signed < 0 ? "−" : ""}
                  {displayPrice(Math.abs(signed), currency)}
                </span>
              </div>

              <Row label="Date">
                {format(new Date(entry.occurredAt), "dd MMM yyyy · HH:mm:ss", {
                  locale: fr,
                })}
              </Row>

              <Row label="Par">{entry.actor?.fullName ?? "—"}</Row>

              {(() => {
                const order = resolveLedgerOrder(entry);
                if (!order) return null;
                return (
                  <Row label="Commande">
                    <button
                      type="button"
                      onClick={() => onOpenOrder?.(order)}
                      className="font-semibold text-primary hover:underline"
                    >
                      #{order.orderNumber}
                    </button>
                  </Row>
                );
              })()}

              {reasonLabel && (
                <Row label="Raison">
                  {expenseUuid && onOpenExpense ? (
                    <button
                      type="button"
                      onClick={() => onOpenExpense(expenseUuid)}
                      className="font-semibold text-primary hover:underline"
                    >
                      {reasonLabel}
                    </button>
                  ) : (
                    <p className="whitespace-pre-wrap text-sm text-card-foreground">
                      {reasonLabel}
                    </p>
                  )}
                </Row>
              )}

              {metadataEntries.map(([key, value]) => (
                <Row key={key} label={LEDGER_METADATA_LABELS[key]}>
                  {String(value)}
                </Row>
              ))}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Row = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <div className="text-sm text-card-foreground">{children}</div>
  </div>
);

export default LedgerEntryDrawer;
